/**
 * BrandLogo — renders the **actual CardAssist logo image** (no programmatic
 * text). The lockup is just the supplied PNG, swapped by theme:
 *
 *   • `/brand/cardassist-logo-light.png`  — LIGHT mode  (black + teal)
 *   • `/brand/cardassist-logo-dark.png`   — DARK  mode  (white + teal)
 *
 * Props
 *   variant   — 'wordmark' (default) | 'mark' (square crop for icons)
 *   height    — render height in px (width auto-scales). Default 52.
 *   tagline   — optional small text BELOW the lockup (e.g. legal sub-text).
 *   to / asLink — wrap in a router <Link>.
 *
 * Note (Dec 2026): the legacy `.online` eyebrow suffix has been removed at
 * the user's request. The `suffix` prop is accepted for backwards
 * compatibility but is now ignored.
 */
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTheme } from '@/lib/theme';

const FULL_LIGHT = '/brand/cardassist-logo-light.png';
const FULL_DARK  = '/brand/cardassist-logo-dark.png';
const MARK_LIGHT = '/brand/icon-light-256.png';
const MARK_DARK  = '/brand/icon-dark-256.png';

const useLogoSrc = () => {
  let theme = 'dark';
  try {
    const ctx = useTheme();
    if (ctx && ctx.theme) theme = ctx.theme;
  } catch (_) { /* fallback when rendered outside ThemeProvider */ }
  return {
    full: theme === 'dark' ? FULL_DARK : FULL_LIGHT,
    mark: theme === 'dark' ? MARK_DARK : MARK_LIGHT,
  };
};

export const BrandWordmark = ({ height = 52, className = '', alt = 'CardAssist' }) => {
  const { full } = useLogoSrc();
  return (
    <img
      src={full}
      alt={alt}
      style={{ height, width: 'auto' }}
      className={cn('block object-contain shrink-0', className)}
      data-testid="brand-wordmark"
      draggable="false"
    />
  );
};

export const BrandMark = ({ size = 44, className = '', alt = 'CardAssist' }) => {
  const { mark } = useLogoSrc();
  return (
    <img
      src={mark}
      alt={alt}
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className={cn('inline-block object-contain shrink-0', className)}
      data-testid="brand-mark"
      draggable="false"
    />
  );
};

export const BrandLogo = ({
  variant = 'wordmark',
  height = 52,
  // Accepted for backwards-compatibility — no longer rendered.
  suffix: _suffixIgnored = null, // eslint-disable-line no-unused-vars
  tagline = null,             // pass a string to render below the lockup
  className = '',
  to = null,
  asLink = false,
  testId = 'brand-logo',
}) => {
  const lockup =
    variant === 'mark' ? (
      <BrandMark size={height} />
    ) : (
      <BrandWordmark height={height} />
    );

  const node = (
    <span
      className={cn('inline-flex flex-col gap-1 select-none items-start', className)}
      data-testid={testId}
    >
      {lockup}
      {tagline && (
        <span className="text-[11px] text-muted-foreground leading-none pl-[2px] tracking-wide">
          {tagline}
        </span>
      )}
    </span>
  );

  const linkTarget = to || (asLink ? '/' : null);
  if (linkTarget) {
    return (
      <Link to={linkTarget} className="no-underline" data-testid={`${testId}-link`}>
        {node}
      </Link>
    );
  }
  return node;
};

export default BrandLogo;
